'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useQueryParam } from './useQueryParam';
import { sendWebEvent } from '@/lib/analytics/web-event-client';
import { gradeGenitive, type LibraryGrade } from '@/lib/library/materials';

type Props = {
  slug: string;
  grade: LibraryGrade;
  /** «PDF · 1 страница · 480 КБ» */
  fileLabel: string;
  /** Сколько материалов в классе всего, включая этот. */
  gradeCount: number;
};

/**
 * Блок скачивания public-материала. После клика файл скачивается, а блок
 * меняет состояние на месте, без модального окна (спека §15): следующий шаг
 * «все материалы класса», вторично диагностика.
 */
export function MaterialDownloadPanel({ slug, grade, fileLabel, gradeCount }: Props) {
  const [done, setDone] = useState(false);
  // Download-обработчик при пропавшем файле возвращает сюда с ?oshibka=fajl.
  const fileError = useQueryParam('oshibka') === 'fajl';

  const href = `/api/library/${slug}/download?from=material_page`;

  if (fileError) {
    return (
      <div className="rounded-[20px] border border-graphite-15 bg-white p-5 sm:p-6" role="alert">
        <p className="font-display text-lg font-extrabold text-graphite">
          Файл сейчас не скачивается
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-graphite-80">
          Мы уже знаем об ошибке и чиним. Попробуйте через несколько минут или напишите на{' '}
          <a className="font-semibold text-teal-dark underline" href="mailto:info@english-klass.ru">
            info@english-klass.ru
          </a>
          , пришлём материал письмом.
        </p>
      </div>
    );
  }

  if (!done) {
    return (
      <div className="rounded-[20px] border border-graphite-8 bg-white p-5 sm:p-6">
        <a
          href={href}
          onClick={() => setDone(true)}
          className="flex min-h-[52px] w-full items-center justify-center rounded-3xl bg-teal px-6 text-base font-semibold text-white transition-colors hover:bg-teal-dark"
        >
          Скачать бесплатно
        </a>
        <p className="mt-3 text-center text-[13.5px] text-graphite-55 sm:text-left">
          {fileLabel}. Регистрация не нужна.
        </p>
      </div>
    );
  }

  const more = gradeCount > 1;
  return (
    <div className="rounded-[20px] border border-graphite-15 bg-white p-5 sm:p-6" aria-live="polite">
      <p className="flex items-center gap-2.5">
        <span
          className="grid h-[22px] w-[22px] place-items-center rounded-full bg-badge-done-bg text-[13px] font-bold text-badge-done-txt"
          aria-hidden
        >
          ✓
        </span>
        <span className="font-display text-[19px] font-extrabold tracking-[-0.02em] text-graphite">
          Готово, файл скачан
        </span>
      </p>
      <p className="mt-2.5 text-[15px] leading-relaxed text-graphite-80">
        {more
          ? `Хотите ещё материалы ${gradeGenitive(grade)}?`
          : 'В библиотеке есть материалы и для других классов.'}
      </p>
      <Link
        href={more ? `/biblioteka?grade=${grade}` : '/biblioteka'}
        className="mt-4 flex min-h-[48px] w-full items-center justify-center rounded-3xl bg-teal px-6 text-[15px] font-semibold text-white transition-colors hover:bg-teal-dark"
      >
        {more ? `Открыть все материалы ${gradeGenitive(grade)}` : 'Открыть все материалы'}
      </Link>
      <Link
        href="/diagnostic"
        data-goal="diag-from-biblioteka"
        className="mt-3 block text-[14.5px] font-semibold text-teal-dark hover:text-graphite"
      >
        Не знаете, что повторять? Проверить английский ребёнка →
      </Link>
      <a
        href={href}
        className="mt-3 inline-block text-[13px] text-graphite-50 hover:text-graphite"
        onClick={() => setDone(true)}
      >
        Скачать файл снова
      </a>
    </div>
  );
}

/**
 * Гейт account-материала для гостя (спека §5C): ценность и вход в продукт,
 * а не «доступ запрещён» и не продажа подписки.
 */
export function MaterialAccountGate({ slug }: { slug: string }) {
  useEffect(() => {
    sendWebEvent({ name: 'library_gate_view', subject: slug, surface: 'material_page' });
  }, [slug]);

  const registerHref = `/register?source=library&material=${encodeURIComponent(slug)}`;
  const loginHref = `/login?material=${encodeURIComponent(slug)}`;
  return (
    <div className="rounded-[20px] border border-graphite-8 bg-white p-5 sm:p-6">
      <p className="font-display text-[11px] font-bold uppercase tracking-[0.1em] text-graphite-45">
        Доступ по аккаунту
      </p>
      <p className="mt-2.5 text-[15px] leading-relaxed text-graphite-80">
        Этот материал доступен родителям с аккаунтом «Английский — Класс!». Регистрация бесплатная,
        карта не нужна. Ссылку на материал пришлём на почту.
      </p>
      <Link
        href={registerHref}
        onClick={() =>
          sendWebEvent({ name: 'library_register_click', subject: slug, surface: 'material_page' })
        }
        className="mt-4 flex min-h-[52px] w-full items-center justify-center rounded-3xl bg-teal px-6 text-base font-semibold text-white transition-colors hover:bg-teal-dark"
      >
        Открыть материалы
      </Link>
      <p className="mt-3 text-center text-[13.5px] text-graphite-70">
        Уже есть аккаунт?{' '}
        <Link
          href={loginHref}
          onClick={() =>
            sendWebEvent({ name: 'library_login_click', subject: slug, surface: 'material_page' })
          }
          className="font-semibold text-teal-dark underline-offset-4 hover:underline"
        >
          Получить ссылку для входа
        </Link>
      </p>
    </div>
  );
}
